import { createFileRoute } from "@tanstack/react-router";
import { motion } from "framer-motion";

export const Route = createFileRoute("/(public)/about")({
  staticData: { title: "About" },
  component: AboutPage,
});

// --- Background ---

function PageBackground() {
  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none select-none z-0">
      {/* Layered radial gradients for depth */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_100%_60%_at_50%_20%,rgba(31,251,245,0.05),transparent_70%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(31,251,240,0.03),transparent_40%)]" />

      {/* Subtle grid overlay */}
      <div
        className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(31,251,221,0.2) 1px, transparent 1px), linear-gradient(90deg, rgba(31,251,221,0.2) 1px, transparent 1px)",
          backgroundSize: "100px 100px",
        }}
      />

      {/* Floating particles with varied sizes */}
      <div
        className="absolute top-1/4 left-1/4 w-1 h-1 bg-brand-400/40 rounded-full animate-float"
        style={{ animationDelay: "0s", animationDuration: "3s" }}
      />
      <div
        className="absolute top-1/3 right-1/3 w-1.5 h-1.5 bg-brand-400/30 rounded-full animate-float"
        style={{ animationDelay: "1s", animationDuration: "4s" }}
      />
      <div
        className="absolute top-1/2 left-[5%] w-1 h-1 bg-brand-400/50 rounded-full animate-float"
        style={{ animationDelay: "2s", animationDuration: "5s" }}
      />
      <div
        className="absolute top-1/2 right-1/4 w-2 h-2 bg-brand-400/20 rounded-full animate-float"
        style={{ animationDelay: "1.5s", animationDuration: "6s" }}
      />
      <div
        className="absolute top-2/3 right-[8%] w-1 h-1 bg-brand-400/40 rounded-full animate-float"
        style={{ animationDelay: "0.5s", animationDuration: "4.5s" }}
      />
    </div>
  );
}

// --- Components ---

function TeamCard({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return (
    <div
      className={`relative border border-surface-700/50 bg-surface-800/50 overflow-hidden rounded-xl backdrop-blur-sm ${className}`}
    >
      {children}
    </div>
  );
}

// --- Data ---

const team = [
  {
    name: "Phyr",
    tag: "Team",
    role: "Co-Founder",
    bio: "Database engineer by day, competitive gamer by night. I've always been driven by competition and the communities that grow around it. Now I'm building the tools and foundation those communities need to thrive.",
    accentColor: "rgb(38, 217, 181)",
    borderAccent: "brand-500",
    image: "/phyr.jpg",
  },
  {
    name: "Apoc",
    tag: "Team",
    role: "Co-Founder",
    bio: "Full-stack engineer who's spent 20+ years chasing hard problems, from C/C++ and malware reverse engineering to modern web and Kubernetes. Now I'm building the competitive platform communities have been missing.",
    accentColor: "rgb(38, 217, 181)",
    borderAccent: "brand-500",
    image: "/apoc.png",
  },
];

// --- Main Page ---

function AboutPage() {
  return (
    <div className="relative min-h-screen bg-surface-950 font-sans selection:bg-brand-500/30 selection:text-brand-200">
      <PageBackground />

      <div className="relative z-10">
        {/* Hero */}
        <section className="pt-32 px-6">
          <div className="mx-auto max-w-3xl">
            <motion.span
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-brand-500 font-mono text-sm uppercase tracking-[0.3em] mb-6 block"
            >
              About Rivaled
            </motion.span>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05, duration: 0.6 }}
              className="text-3xl md:text-6xl font-display font-black tracking-tight text-white leading-[1.1] mb-8"
            >
              Competitive platform for{" "}
              <span className="relative inline-block text-brand-400">
                gaming communities.
                <span className="absolute -bottom-1 left-0 right-0 h-[2px] bg-gradient-to-r from-brand-400 via-brand-500 to-transparent" />
              </span>
            </motion.h1>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15, duration: 0.6 }}
              className="space-y-5 text-lg text-surface-300 leading-relaxed"
            >
              <p>
                Today, thousands of competitive gaming communities run their entire operation
                through Discord. ELO bots handle basic rankings and matchmaking, but everything
                lives as plain text dumped into chat channels. Leaderboards become walls of names
                and numbers. Match results scroll past and disappear. There is no structured way to
                browse stats, compare players, or track progress over time. Google Sheets track
                rosters, match history, and season standings. Social interaction is scattered across
                chats, while organizers manually update scores and resolve disputes from
                screenshots.
              </p>
              <p>
                It works, until it doesn't. Bots break with Discord updates. Spreadsheets get
                corrupted. Matchmaking is shallow, stat tracking is limited, and social features are
                fragmented. Scaling means burying organizers in admin work. Communities outgrow
                these tools quickly, but there has never been a real alternative. So we built
                Rivaled.
              </p>
              <p>
                Rivaled is a platform purpose built for competitive communities. Matchmaking,
                rankings, rosters, seasons, social interaction, and community management live in one
                place. No more stitching together bots and spreadsheets. Organizers focus on their
                community instead of fighting their tools, and players get the competitive
                experience they actually deserve.
              </p>
            </motion.div>
          </div>
        </section>

        {/* The Team */}
        <section className="px-6 pt-16 md:pt-12 pb-10">
          <div className="mx-auto max-w-3xl">
            <motion.span
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-brand-500 font-mono text-sm uppercase tracking-[0.3em] mb-6 block"
            >
              The Team
            </motion.span>

            <motion.h2
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.05 }}
              className="text-3xl md:text-4xl font-display font-bold text-white tracking-tight mb-10"
            >
              Built by players, for players.
            </motion.h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {team.map((person, i) => (
                <motion.div
                  key={person.name}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.15, duration: 0.5 }}
                >
                  <TeamCard className="h-full">
                    <div className="flex flex-col h-full relative">
                      {/* Header */}
                      <div className="p-6 border-b border-surface-800 bg-surface-950/40 relative z-10">
                        <div className="flex items-center gap-4">
                          {/* Avatar */}
                          <div className="relative flex-shrink-0">
                            <img
                              src={person.image}
                              alt={person.name}
                              className="w-12 h-12 rounded-lg object-cover"
                            />
                          </div>
                          <div>
                            <h3 className="text-xl font-display font-bold text-white">
                              {person.name}
                            </h3>
                            <p
                              className="font-mono text-xs mt-0.5"
                              style={{
                                color: person.accentColor,
                              }}
                            >
                              {person.role}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Body */}
                      <div className="p-6 flex-1 relative z-10">
                        <p className="text-surface-300 text-sm leading-relaxed">{person.bio}</p>
                      </div>
                    </div>
                  </TeamCard>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
